import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {StageRunTypedValue, StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {
  BaseLookupDefinition,
  LookupSpecifier,
  SampleSpecifier,
  SourceSpecifier,
} from './LookupFieldDefinitionConstants';

export const LOOKUP_FIELD_FIELD_NAME = 'FIELD_NAME';
export const LOOKUP_FIELD_FIELD_TYPE = 'FIELD_TYPE';
export const LOOKUP_FIELD_FILTER_TYPE = 'FIELD_VALUE';
export const LOOKUP_FIELD_SCHEMA_ID = 'SCHEMA_ID';
export const LOOKUP_FIELD_SOURCE = 'SOURCE';

type LookupFieldKeyword =
  | 'FIELD_NAME'
  | 'FIELD_TYPE'
  | 'FILTER_TYPE'
  | 'SCHEMA_ID'
  | 'SOURCE'
  | 'FIELD_VALUE';

interface LookupFieldParam {
  FIELD_NAME?: Array<string>;
  FIELD_TYPE?: Array<string>;
  FILTER_TYPE?: Array<string>;
  SCHEMA_ID?: Array<string>;
  SOURCE?: Array<string | TypedValue | ast.FunctionOperator>;
  FIELD_VALUE?: Array<string>;
  [key: string]: Array<string> | Array<string | ast.FunctionOperator | TypedValue> | undefined;
}

const NUM_ARGS_BY_KEYWORD: {[keyword in keyof LookupFieldParam]: number} = {
  [LOOKUP_FIELD_FIELD_NAME]: 1,
  [LOOKUP_FIELD_FIELD_TYPE]: 1,
  [LOOKUP_FIELD_FILTER_TYPE]: 2,
  [LOOKUP_FIELD_SCHEMA_ID]: 1,
  [LOOKUP_FIELD_SOURCE]: 1,
};

export const LOOKUP_FIELD_FIELD_TYPE_EXP_COND = 'EXPERIMENTAL_CONDITION';
export const LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH = 'INPUT_SAMPLE_BATCH';
export const LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_ENTITY = 'INPUT_SAMPLE_ENTITY';
export const LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH_ENTITY = 'INPUT_SAMPLE_BATCH_ENTITY';
export const LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH = 'SAMPLE_BATCH';
export const LOOKUP_FIELD_FIELD_TYPE_SAMPLE_ENTITY = 'SAMPLE_ENTITY';
export const LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH_ENTITY = 'SAMPLE_BATCH_ENTITY';
export const STAGE_RUN_LOOKUP_FIELD_FIELD_TYPES = [
  LOOKUP_FIELD_FIELD_TYPE_EXP_COND,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_ENTITY,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH_ENTITY,
];
export const SAMPLE_LOOKUP_FIELD_FIELD_TYPES = [
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH,
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_ENTITY,
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH_ENTITY,
];

function getAtIndex<T>(list: Array<T>, index: number): T {
  if (index >= list.length) {
    throw new ExcessUtil.FormulaParseError('Invalid arguments.');
  }
  return list[index];
}

/**
 * Return the string representing the value of the child at the specific index.
 *
 * We are assuming that the child node is a `ast.Value` node and that the `rawValue` is a string
 */
function getRawValueFromAstValueChildNode(children: Array<ast.ASTNode>, index: number): string | null {
  const node = getAtIndex(children, index);
  if (!(node instanceof ast.Value)) {
    // @ts-expect-error Not all ASTNodes have 'type property, but it's ok because we are checking it
    const nodeType = node.type;
    if (nodeType) {
      throw new ExcessUtil.FormulaParseError(`Invalid argument type ${nodeType} for LOOKUPFIELD`);
    } else {
      throw new ExcessUtil.FormulaParseError(`Invalid argument for LOOKUPFIELD`);
    }
  }
  const evaluatedValue = node.eval();
  // eslint-disable-next-line benchlint/no-in-operator
  const rawValue = 'rawValue' in evaluatedValue ? evaluatedValue.rawValue : null;
  assertAndContinue(
    // eslint-disable-next-line no-restricted-syntax
    rawValue == null || _.isString(rawValue),
    'Child node rawValue is expected to be string',
    {rawValue}
  );
  return rawValue as string | null;
}

/**
 * Answer whether the specified argValue is a valid LookupFieldKeyword
 */
function isKeyword(
  argValue: string | number | Array<TypedValue> | undefined | null
): argValue is LookupFieldKeyword {
  // eslint-disable-next-line no-restricted-syntax
  return _.isString(argValue) && Object.keys(NUM_ARGS_BY_KEYWORD).includes(argValue);
}

/**
 * Transform the children on the specified node into LookupFieldParam that can be later easily
 * parsed to evaluate the value of the LOOKUPFIELD represented by the node
 */
export function getChildrenAsKeywordArgs(node: ast.FunctionOperator): LookupFieldParam {
  const kwargs: LookupFieldParam = {};

  let i = 0;
  while (i < node.children.length) {
    const keyword = getRawValueFromAstValueChildNode(node.children, i);
    const valueNode = getAtIndex(node.children, i + 1);
    let values;
    if (!isKeyword(keyword)) {
      assertAndContinue(false, `Unexpected keyword ${keyword}`);
      return kwargs;
    }
    const numValues = NUM_ARGS_BY_KEYWORD[keyword];
    if (numValues == null) {
      throw new ExcessUtil.FormulaParseError(`Invalid keyword ${keyword} in LOOKUPFIELD`);
    }
    if ((valueNode as ast.FunctionOperator).type === 'LOOKUPFIELD') {
      values = [valueNode as ast.FunctionOperator];
    } else {
      values = kwargs[keyword] || [];
      for (let valueIndex = 0; valueIndex < numValues; valueIndex++) {
        const rawValue = getRawValueFromAstValueChildNode(node.children, i + 1 + valueIndex);
        if (rawValue != null) {
          values.push(rawValue);
        }
      }
    }
    if (values.length > 0) {
      // @ts-expect-error: values has the same type as kwargs[keyword] but TS doesn't know that
      kwargs[keyword] = values;
    }
    i++; // keyword
    i += numValues;
  }
  return kwargs;
}

/**
 * Parser for the legacy LOOKUPFIELD AST nodes that use schema names instead of IDs.
 */
function parseLookupSpecifierLegacy(
  node: ast.FunctionOperator,
  schemaName: string | null,
  fieldName: string | null
): LookupSpecifier | null {
  const filterSpecifiers = [];
  let sourceSpecifier = null;

  let childIndex = 2;
  while (childIndex < node.children.length) {
    const rawValue = getRawValueFromAstValueChildNode(node.children, childIndex++);

    if (rawValue === LOOKUP_FIELD_SOURCE) {
      const sourceNode = getAtIndex(node.children, childIndex++);
      if ((sourceNode as ast.FunctionOperator).type !== 'LOOKUPFIELD') {
        return null;
      }
      const sourceChildren = (sourceNode as ast.FunctionOperator).children;
      const sourceSchemaName = getRawValueFromAstValueChildNode(sourceChildren, 0);
      const sourceFieldName = getRawValueFromAstValueChildNode(sourceChildren, 1);
      const sourceLookupSpecifier = parseLookupSpecifierLegacy(
        sourceNode as ast.FunctionOperator,
        sourceSchemaName,
        sourceFieldName
      );
      if (sourceLookupSpecifier != null) {
        sourceSpecifier = {
          lookupSpecifier: sourceLookupSpecifier,
        };
      }
    } else if (rawValue === LOOKUP_FIELD_FILTER_TYPE) {
      const filterFieldName = getRawValueFromAstValueChildNode(node.children, childIndex++);
      const filterFieldValue = getRawValueFromAstValueChildNode(node.children, childIndex++);

      filterSpecifiers.push({
        fieldFilter: {
          name: filterFieldName,
          value: filterFieldValue,
        },
      });
    } else {
      return null;
    }
  }

  return {
    schemaName,
    schemaId: null,
    fieldName,
    fieldType: null,
    filterSpecifiers,
    sourceSpecifier,
  };
}

/**
 * Parse the node arguments and extract the lookup specifier from them.
 *
 * The type of the node has to be 'LOOKUPFIELD'.
 * Return null if parsing the arguments failed.
 */
export function parseLookupSpecifierFromLookupFieldNode(node: ast.FunctionOperator): LookupSpecifier | null {
  assert(node.type === 'LOOKUPFIELD', 'Node type is expected to be LOOKUPFIELD');

  const arg0 = getRawValueFromAstValueChildNode(node.children, 0);

  if (arg0 === 'SCHEMA_ID' || arg0 === 'FIELD_TYPE') {
    // This is a new style formula introduced 2017-02. See https://benchling-jira.atlassian.net/browse/BNCH-720
    const kwargs = getChildrenAsKeywordArgs(node);

    let fieldName = null;
    if (kwargs.FIELD_NAME) {
      [fieldName] = kwargs.FIELD_NAME;
    }

    let fieldType = null;
    if (kwargs.FIELD_TYPE) {
      [fieldType] = kwargs.FIELD_TYPE;
    }

    let schemaId = null;
    if (kwargs.SCHEMA_ID) {
      [schemaId] = kwargs.SCHEMA_ID;
    }

    const filterSpecifiers = [];
    if (kwargs.FIELD_VALUE) {
      for (let i = 0; i < kwargs.FIELD_VALUE.length; i += 2) {
        const [dataTableColumnName, filterValue] = kwargs.FIELD_VALUE.slice(i, i + 2);
        filterSpecifiers.push({fieldFilter: {name: dataTableColumnName, value: filterValue}});
      }
    }

    let sourceSpecifier = null;
    let source = null;
    if (kwargs.SOURCE) {
      [source] = kwargs.SOURCE;
      // TODO(ala): I think we should be able to remove this if statement, especially since it seems
      // like there is a bug because the type is inconsistent with parseLookupFieldArgValues
      if (source === 'STAGE_RUN') {
        sourceSpecifier = null; // Use the sample/stage run from the row.
      } else if ((source as ast.FunctionOperator).type === 'LOOKUPFIELD') {
        const sourceLookupSpecifier = parseLookupSpecifierFromLookupFieldNode(source as ast.FunctionOperator);
        if (sourceLookupSpecifier != null) {
          sourceSpecifier = {lookupSpecifier: sourceLookupSpecifier};
        }
      }
    }

    if (!fieldName && !fieldType) {
      return null;
    }
    if (
      fieldType &&
      fieldType !== LOOKUP_FIELD_FIELD_TYPE_EXP_COND &&
      !STAGE_RUN_LOOKUP_FIELD_FIELD_TYPES.includes(fieldType) &&
      !SAMPLE_LOOKUP_FIELD_FIELD_TYPES.includes(fieldType)
    ) {
      return null;
    }
    if (fieldType === LOOKUP_FIELD_FIELD_TYPE_EXP_COND && !fieldName) {
      return null;
    }
    if (source && source !== 'STAGE_RUN' && (source as ast.FunctionOperator).type !== 'LOOKUPFIELD') {
      return null;
    }

    return {
      schemaName: null,
      schemaId,
      fieldName,
      fieldType,
      filterSpecifiers,
      sourceSpecifier,
    };
  } else {
    // This is a legacy formula.
    const fieldName = getRawValueFromAstValueChildNode(node.children, 1);
    return parseLookupSpecifierLegacy(node, arg0, fieldName);
  }
}

/**
 * Return sample specifiers represented by the specified typedValue. The typed value should be either
 * BioentityLinkSchemaFieldTypedValue or BatchLinkSchemaFieldTypedValue to allow extracting sample specifiers
 */
export function getAllSampleSpecifiersFromTypedValue(typedValue: TypedValue): Array<SampleSpecifier> {
  const sampleSpecifiers = [];
  const targetFiles = typedValue.type === FieldType.BIOENTITY_LINK ? typedValue.targetFiles : null;
  if (targetFiles != null) {
    for (const targetFile of targetFiles) {
      sampleSpecifiers.push({
        batchId: null,
        fileId: targetFile.id,
      });
    }
  }
  const targetBatches = typedValue.type === FieldType.BATCH_LINK ? typedValue.targetBatches : null;
  if (targetBatches != null) {
    for (const targetBatch of targetBatches) {
      sampleSpecifiers.push({
        batchId: targetBatch.id,
        fileId: null,
      });
    }
  }
  return sampleSpecifiers;
}

export function getPositionalArgsAsKeywordArgs(args: Array<TypedValue>): LookupFieldParam {
  const kwargs: LookupFieldParam = {};

  let i = 0;
  while (i < args.length) {
    const keyword = (args[i] as StringTypedValue).rawValue;
    if (!isKeyword(keyword)) {
      assertAndContinue(false, `Unexpected keyword ${keyword}`);
      return kwargs;
    }
    const values = kwargs[keyword] || [];
    const numValues = NUM_ARGS_BY_KEYWORD[keyword] || 0;
    if (keyword === LOOKUP_FIELD_SOURCE) {
      values.push(getAtIndex(args, i + 1));
    } else {
      for (let valueIndex = 0; valueIndex < numValues; valueIndex++) {
        values.push((getAtIndex(args, i + 1 + valueIndex) as StringTypedValue).rawValue);
      }
    }
    // @ts-expect-error: values has the same type as kwargs[keyword] but TS doesn't know that
    kwargs[keyword] = values;
    i++;
    i += numValues;
  }
  return kwargs;
}

/**
 * Parser for the legacy LOOKUPFIELD arguments that use schema names instead of IDs.
 */
function parseLookupFieldLegacyArgValues(
  schemaName: TypedValue,
  fieldName: TypedValue,
  additionalArgs: Array<TypedValue>
): [BaseLookupDefinition & {sampleSpecifiers: Array<SampleSpecifier> | null}] {
  let argIndex = 0;
  const filterSpecifiers = [];
  let sampleSpecifiers: Array<SampleSpecifier> | null = null;

  while (argIndex < additionalArgs.length) {
    const argValue = additionalArgs[argIndex++];
    const {rawValue} = argValue as StringTypedValue;
    if (rawValue === LOOKUP_FIELD_SOURCE) {
      const sourceSampleTypedValue = getAtIndex(additionalArgs, argIndex++);
      sampleSpecifiers = getAllSampleSpecifiersFromTypedValue(sourceSampleTypedValue);
    } else if (rawValue === LOOKUP_FIELD_FILTER_TYPE) {
      const filterFieldName = (getAtIndex(additionalArgs, argIndex++) as StringTypedValue).rawValue;
      const filterFieldValue = (getAtIndex(additionalArgs, argIndex++) as StringTypedValue).rawValue;

      filterSpecifiers.push({
        fieldFilter: {
          name: filterFieldName,
          value: filterFieldValue,
        },
      });
    } else {
      throw new ExcessUtil.FormulaParseError(`${rawValue} LOOKUPFIELD argument is not supported`);
    }
  }

  return [
    {
      schemaName: (schemaName as StringTypedValue).rawValue,
      schemaId: null,
      fieldName: (fieldName as StringTypedValue).rawValue,
      fieldType: null,
      filterSpecifiers,
      sampleSpecifiers,
    },
  ];
}

/**
 * Parse resolved node arguments and extract lookup field definition from them.
 *
 * In the returned object, sourceSpecifier is already resolved (and replaced by)
 * the corresponding sample specifier based on the value of the resolved SOURCE arg. For a
 * lookup specifier that doesn't have a source specified in additionalArgs, sampleSpecifiers is
 * null, which means that the sample should be taken from the rowData.
 *
 * Throws FormulaParseError if parsing the arguments failed.
 */
export function parseLookupFieldArgValues(
  args: Array<TypedValue>
): [BaseLookupDefinition & {sampleSpecifiers: Array<SampleSpecifier> | null}] {
  if (args.length < 2) {
    throw new ExcessUtil.FormulaParseError('LOOKUPFIELD takes at least 2 arguments');
  }
  const [arg0, arg1, ...additionalArgs] = args;
  if (
    (arg0 as StringTypedValue).rawValue === 'SCHEMA_ID' ||
    (arg0 as StringTypedValue).rawValue === 'FIELD_TYPE'
  ) {
    // This is a new style formula introduced 2017-02. See https://benchling-jira.atlassian.net/browse/BNCH-720
    // It's possible for new style formulas to have arguments in a different order like
    // LOOKUPFIELD("FIELD_NAME", "x", "SCHEMA_ID", "y")
    // but we don't support that (almost all LOOKUPFIELD formulas will be generated by the modal).
    const kwargs = getPositionalArgsAsKeywordArgs(args);

    let fieldName = null;
    if (kwargs.FIELD_NAME) {
      [fieldName] = kwargs.FIELD_NAME;
    }

    let fieldType = null;
    if (kwargs.FIELD_TYPE) {
      [fieldType] = kwargs.FIELD_TYPE;
    }

    let schemaId = null;
    if (kwargs.SCHEMA_ID) {
      [schemaId] = kwargs.SCHEMA_ID;
    }

    const filterSpecifiers = [];
    if (kwargs.FIELD_VALUE) {
      for (let i = 0; i < kwargs.FIELD_VALUE.length; i += 2) {
        const [dataTableColumnName, filterValue] = kwargs.FIELD_VALUE.slice(i, i + 2);
        filterSpecifiers.push({fieldFilter: {name: dataTableColumnName, value: filterValue}});
      }
    }

    let sampleSpecifiers = null;
    if (kwargs.SOURCE) {
      const [source] = kwargs.SOURCE;
      if (
        (source as TypedValue).type === BaseTypedValueType.STAGE_RUN &&
        (source as StageRunTypedValue).stageRunId != null
      ) {
        sampleSpecifiers = [{stageRunId: (source as StageRunTypedValue).stageRunId}];
      } else if ((source as StringTypedValue).rawValue === 'STAGE_RUN') {
        sampleSpecifiers = null; // Use stageRunId from the row.
      } else {
        // Assume source is a sample specifier from a nested LOOKUPFIELD.
        sampleSpecifiers = getAllSampleSpecifiersFromTypedValue(source as TypedValue);
      }
    }

    if (!fieldName && !fieldType) {
      throw new ExcessUtil.FormulaParseError('LOOKUPFIELD requires FIELD_NAME or FIELD_TYPE');
    }
    if (
      fieldType &&
      !STAGE_RUN_LOOKUP_FIELD_FIELD_TYPES.includes(fieldType) &&
      !SAMPLE_LOOKUP_FIELD_FIELD_TYPES.includes(fieldType)
    ) {
      throw new ExcessUtil.FormulaParseError(
        'LOOKUPFIELD FIELD_TYPE must be EXPERIMENTAL_CONDITION, ' +
          'INPUT_SAMPLE_BATCH, INPUT_SAMPLE_ENTITY, INPUT_SAMPLE_BATCH_ENTITY, ' +
          'SAMPLE_BATCH, SAMPLE_ENTITY, or SAMPLE_BATCH_ENTITY.'
      );
    }
    if (fieldType === LOOKUP_FIELD_FIELD_TYPE_EXP_COND && !fieldName) {
      throw new ExcessUtil.FormulaParseError('LOOKUPFIELD for experimental condition requires FIELD_NAME');
    }

    return [
      {
        schemaName: null,
        schemaId,
        fieldName,
        fieldType,
        filterSpecifiers,
        sampleSpecifiers,
      },
    ];
  } else {
    // This is a legacy formula.
    return parseLookupFieldLegacyArgValues(arg0, arg1, additionalArgs);
  }
}

export function compileLookupArgs(
  lookupArgs: Array<string | null>,
  sourceSpecifier: SourceSpecifier | null | undefined
): string {
  const compiledArgs = lookupArgs.map((arg) => `"${arg}"`).join(',');

  if (sourceSpecifier == null) {
    return compiledArgs;
  }

  return `${compiledArgs},"${LOOKUP_FIELD_SOURCE}",${compileLookupFieldFunction(
    sourceSpecifier.lookupSpecifier
  )}`;
}

export function compileLookupFieldFunction({
  schemaId,
  fieldName,
  fieldType,
  filterSpecifiers,
  sourceSpecifier,
}: LookupSpecifier): string {
  const lookupArgs = [];
  if (schemaId != null) {
    lookupArgs.push('SCHEMA_ID', schemaId);
  }
  if (fieldType != null) {
    lookupArgs.push('FIELD_TYPE', fieldType);
  }
  if (fieldName != null) {
    lookupArgs.push('FIELD_NAME', fieldName);
  }
  if (filterSpecifiers != null) {
    for (const filterSpecifier of filterSpecifiers) {
      const {fieldFilter} = filterSpecifier;
      lookupArgs.push('FIELD_VALUE', fieldFilter.name, fieldFilter.value);
    }
  }
  return `LOOKUPFIELD(${compileLookupArgs(lookupArgs, sourceSpecifier)})`;
}
